import { Upload as UploadIcon } from "@mui/icons-material";
import { Card, CardContent, Chip, ChipProps, Stack, Typography } from "@mui/material";
import { compact } from "lodash-es";
import { useSnackbar } from "notistack";
import React, { useCallback, useContext } from "react";

import { KycContext } from "../../../contexts/kyc/KycContext";
import { IKycContextApplicationDocumentFragment } from "../../../contexts/kyc/KycContext.generated";
import {
    IApplicationDocumentReasonCode,
    IApplicationDocumentStatus,
    IApplicationDocumentType,
} from "../../../generated/types";
import { logNever } from "../../../utils/assertNever";
import { CombinedErrorAlert } from "../../combined-error-alert/CombinedErrorAlert";
import { AddressDisplay } from "../../display/AddressDisplay";
import { IVouchedConfig } from "../../vouched/loadVouched";
import { VouchedButton } from "../../vouched/VouchedButton";
import { useApplicationDocumentsVerifyDocumentMutation } from "./ApplicationDocuments.generated";
import { DocumentSide, UploadDocumentButton } from "./UploadDocumentButton";

interface IProps {
    applicationId: string;
    documents: IKycContextApplicationDocumentFragment[];
    maxFileSizeBytes: number;
}

export const ApplicationDocuments: React.FC<IProps> = ({ applicationId, documents, maxFileSizeBytes }) => {
    return (
        <Stack spacing={2}>
            {documents.map((doc) => (
                <ApplicationDocument
                    key={doc.id}
                    applicationId={applicationId}
                    document={doc}
                    maxFileSizeBytes={maxFileSizeBytes}
                />
            ))}
        </Stack>
    );
};

interface IApplicationDocumentProps {
    applicationId: string;
    document: IKycContextApplicationDocumentFragment;
    maxFileSizeBytes: number;
}

const ApplicationDocument: React.FC<IApplicationDocumentProps> = ({
    applicationId,
    document: { address, description, id: documentId, ein, documentType, name, passport, reason, reasonCode, status },
    maxFileSizeBytes,
}) => {
    function renderDetails() {
        switch (documentType) {
            case IApplicationDocumentType.IdDocument:
                return null;
            case IApplicationDocumentType.Passport:
                return passport;
            case IApplicationDocumentType.AddressVerification: {
                if (address == null) {
                    return null;
                }

                return <AddressDisplay address={address} />;
            }
            case IApplicationDocumentType.CertificateOfIncorporation:
                return ein;
            case IApplicationDocumentType.EmployerIdentificationNumberConfirmation:
            case IApplicationDocumentType.SocialSecurityCard:
            case IApplicationDocumentType.PowerOfAttorney:
            case IApplicationDocumentType.ClientRequested:
            case IApplicationDocumentType.SelfieVerification:
                return null;
            default:
                return logNever(documentType, null);
        }
    }

    function renderUpload() {
        if (!doesStatusRequireUpload(status)) {
            return null;
        }

        if (documentType === IApplicationDocumentType.SelfieVerification) {
            return <SelfieVerification applicationId={applicationId} documentId={documentId} />;
        }

        return (
            <div>
                <UploadDocumentButton
                    applicationId={applicationId}
                    side={getRequiredDocumentSideFromStatus(status)}
                    documentId={documentId}
                    maxFileSizeBytes={maxFileSizeBytes}
                />
            </div>
        );
    }

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack spacing={2}>
                    <Stack spacing={1}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography fontWeight="semiBold">
                                {UNIT_DOCUMENT_DISPLAY_NAME[documentType]} for {name}
                            </Typography>
                            <Chip {...UNIT_DOCUMENT_STATUS_CHIP_PROPS[status]} />
                        </Stack>
                        <Typography>{description}</Typography>
                    </Stack>
                    <Typography variant="label1">{renderDetails()}</Typography>
                    {status === IApplicationDocumentStatus.Invalid && (reasonCode || reason) && (
                        <Typography color="error.main" variant="label1">
                            {compact([reasonCode != null && UNIT_REASON_DISPLAY_NAME[reasonCode], reason]).join(": ")}
                        </Typography>
                    )}
                    {renderUpload()}
                </Stack>
            </CardContent>
        </Card>
    );
};

interface ISelfieVerificationProps {
    applicationId: string;
    documentId: string;
}

const SelfieVerification: React.FC<ISelfieVerificationProps> = ({ applicationId, documentId }) => {
    const { refetchApplication } = useContext(KycContext);
    const { enqueueSnackbar } = useSnackbar();

    const [{ data, error, fetching }, verifyDocument] = useApplicationDocumentsVerifyDocumentMutation();
    const handleSuccess = useCallback(
        async (jobId: string): Promise<void> => {
            await verifyDocument({ input: { applicationId, documentId, jobId } });
            refetchApplication();
        },
        [applicationId, documentId, verifyDocument, refetchApplication],
    );

    const handleFail = useCallback(
        (errors: IVouchedConfig.IVerificationError[]): void => {
            if (errors.length === 0) {
                return;
            }

            enqueueSnackbar(
                <Stack>
                    {errors.map(({ message }, i) => (
                        <Typography key={i} variant="inherit">
                            {message}
                        </Typography>
                    ))}
                </Stack>,
                { variant: "error" },
            );
        },
        [enqueueSnackbar],
    );

    return (
        <Stack spacing={1}>
            <div>
                <VouchedButton
                    buttonProps={{
                        variant: "outlined",
                        loading: fetching,
                        startIcon: <UploadIcon fontSize="inherit" />,
                    }}
                    onSuccess={handleSuccess}
                    onFail={handleFail}
                />
            </div>
            <CombinedErrorAlert
                title="Error verifying selfie"
                combinedError={error}
                gqlErrors={data?.verifyDocument.errors ?? undefined}
            />
        </Stack>
    );
};

const UNIT_DOCUMENT_DISPLAY_NAME: Record<IApplicationDocumentType, string> = {
    [IApplicationDocumentType.IdDocument]: "ID Document",
    [IApplicationDocumentType.Passport]: "Passport",
    [IApplicationDocumentType.AddressVerification]: "Address Verification",
    [IApplicationDocumentType.CertificateOfIncorporation]: "Certificate of Incorporation",
    [IApplicationDocumentType.EmployerIdentificationNumberConfirmation]: "Employer Identification Number Confirmation",
    [IApplicationDocumentType.SocialSecurityCard]: "Social Security Card",
    [IApplicationDocumentType.PowerOfAttorney]: "Power of Attorney",
    [IApplicationDocumentType.ClientRequested]: "Client Requested",
    [IApplicationDocumentType.SelfieVerification]: "Selfie Verification",
};

const UNIT_DOCUMENT_STATUS_CHIP_PROPS: Record<IApplicationDocumentStatus, ChipProps> = {
    [IApplicationDocumentStatus.Required]: { label: "Required", color: "primary" },
    [IApplicationDocumentStatus.ReceivedBack]: { label: "Received Back", color: "default" },
    [IApplicationDocumentStatus.ReceivedFront]: { label: "Received Front", color: "default" },
    [IApplicationDocumentStatus.Invalid]: { label: "Invalid", color: "error" },
    [IApplicationDocumentStatus.Approved]: { label: "Approved", color: "success" },
    [IApplicationDocumentStatus.PendingReview]: { label: "Pending Review", color: "default" },
};

const UNIT_REASON_DISPLAY_NAME: Record<IApplicationDocumentReasonCode, string> = {
    [IApplicationDocumentReasonCode.PoorQuality]: "Poor Quality",
    [IApplicationDocumentReasonCode.NameMismatch]: "Name Mismatch",
    [IApplicationDocumentReasonCode.SsnMismatch]: "SSN Mismatch",
    [IApplicationDocumentReasonCode.AddressMismatch]: "Address Mismatch",
    [IApplicationDocumentReasonCode.DobMismatch]: "Date of Birth Mismatch",
    [IApplicationDocumentReasonCode.ExpiredId]: "Expired Id",
    [IApplicationDocumentReasonCode.EinMismatch]: "EIN Mismatch",
    [IApplicationDocumentReasonCode.StateMismatch]: "State Mismatch",
    [IApplicationDocumentReasonCode.Other]: "Other",
};

function doesStatusRequireUpload(status: IApplicationDocumentStatus): boolean {
    switch (status) {
        case IApplicationDocumentStatus.Approved:
        case IApplicationDocumentStatus.PendingReview:
            return false;
        case IApplicationDocumentStatus.Invalid:
        case IApplicationDocumentStatus.ReceivedBack:
        case IApplicationDocumentStatus.ReceivedFront:
        case IApplicationDocumentStatus.Required:
            return true;
        default:
            return logNever(status, false);
    }
}

function getRequiredDocumentSideFromStatus(status: IApplicationDocumentStatus): DocumentSide | undefined {
    switch (status) {
        case IApplicationDocumentStatus.Approved:
        case IApplicationDocumentStatus.PendingReview:
        case IApplicationDocumentStatus.Invalid:
        case IApplicationDocumentStatus.Required:
            return undefined;
        case IApplicationDocumentStatus.ReceivedBack:
            return DocumentSide.Front;
        case IApplicationDocumentStatus.ReceivedFront:
            return DocumentSide.Back;
        default:
            return logNever(status, undefined);
    }
}
